import { cloneElement, isValidElement, useEffect, useState, useSyncExternalStore, type ComponentType, type ReactElement, type ReactNode, type SyntheticEvent } from 'react';

export type ItemMenuSuspenso =
  | { tipo?: 'item'; id: string; rotulo: ReactNode; icone?: ReactNode; atalho?: string; perigo?: boolean; desabilitado?: boolean; aoEscolher: () => void }
  | { tipo: 'opcao'; id: string; rotulo: ReactNode; marcado: boolean; aoEscolher: () => void }
  | { tipo: 'separador'; id: string }
  | { tipo: 'titulo'; id: string; rotulo: ReactNode };

export interface MenuSuspensoProps {
  gatilho: ReactNode;
  itens: ItemMenuSuspenso[];
  alinhar?: 'start' | 'end';
  rotulo?: string;
}

/*
 * O primitivo de menu (Radix + floating-ui) custa ~17 KB gzip e só serve quando alguém abre um menu: fica fora do chunk
 * inicial (07 §6.1, D0-154). Carrega quando o navegador fica ocioso; interação antes disso carrega na hora e abre o menu.
 */
type TipoImplementacao = ComponentType<MenuSuspensoProps & { abrirAoMontar?: boolean; focarAoMontar?: boolean }>;

// O módulo carregado fica guardado e é renderizado direto (sem React.lazy): um Suspense entre "carregou" e "renderizou"
// mostraria o gatilho sem tratador e engoliria o clique.
let Implementacao: TipoImplementacao | null = null;
let pedido: Promise<unknown> | null = null;
const ouvintes = new Set<() => void>();

export function precarregarMenus(): Promise<unknown> {
  pedido ??= import('./MenuSuspensoRadix').then((m) => {
    Implementacao = m.default;
    ouvintes.forEach((f) => f());
  });
  return pedido;
}

function usePronto() {
  return useSyncExternalStore((f) => {
    ouvintes.add(f);
    return () => void ouvintes.delete(f);
  }, () => Implementacao !== null, () => false);
}

export function MenuSuspenso(props: MenuSuspensoProps) {
  const carregado = usePronto();
  const [intencao, setIntencao] = useState<{ abrir: boolean; focar: boolean } | null>(null);
  const [focado, setFocado] = useState(false);

  useEffect(() => {
    const ocioso = window.requestIdleCallback ?? ((f: () => void) => window.setTimeout(f, 1));
    ocioso(() => void precarregarMenus());
  }, []);

  if (carregado && Implementacao) {
    const Menu = Implementacao;
    return <Menu {...props} abrirAoMontar={intencao?.abrir} focarAoMontar={intencao?.focar ?? focado} />;
  }
  if (!isValidElement(props.gatilho)) return props.gatilho;
  const gatilho = props.gatilho as ReactElement<Record<string, unknown>>;
  // Antes de carregar: o gatilho é o mesmo botão; abrir (clique, Enter, Espaço, ↓) pede o módulo e abre ao montar.
  const abrir = (e: SyntheticEvent) => {
    e.preventDefault();
    setIntencao({ abrir: true, focar: true });
    void precarregarMenus();
  };
  return cloneElement(gatilho, {
    'aria-haspopup': 'menu',
    'aria-expanded': false,
    onClick: abrir,
    // A troca pelo gatilho do Radix (ao terminar de carregar) devolve o foco se ele estava aqui.
    onFocus: () => setFocado(true),
    onBlur: () => setFocado(false),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') abrir(e);
    },
  });
}
